import { ReactNode } from 'react';
import { Box, Text, Tooltip, useComputedColorScheme } from '@mantine/core';

// Hand-rolled SVG charts for the admin Activation & Retention overview.
// No chart library: the shapes here are bars, lines, and cells, and keeping
// them as plain SVG keeps the bundle small and the theming exact.

// ---------------------------------------------------------------------------
// Types mirrored from the backend /api/admin/stats response
// ---------------------------------------------------------------------------

export type SwapCohortStats = {
  users: number;
  activeLast30Days: number;
  createdMultiplePlans: number;
  stillSubscribed: number;
};

export type AdminUserStats = {
  totals: {
    users: number;
    completedOnboarding: number;
    createdPlan: number;
    activatedPlan: number;
    createdMultiplePlans: number;
    swappedMeal: number;
    activeLast7Days: number;
    activeLast30Days: number;
    signupsLast7Days: number;
    signupsLast30Days: number;
  };
  subscriptions: {
    trial: number;
    active: number;
    canceled: number;
    past_due: number;
    lifetime: number;
  };
  planCountBuckets: {
    '0': number;
    '1': number;
    '2': number;
    '3-5': number;
    '6+': number;
  };
  swapComparison: {
    swapped: SwapCohortStats;
    notSwapped: SwapCohortStats;
  };
  weeklyCohorts: Array<{
    weekStart: string;
    signups: number;
    completedOnboarding: number;
    createdPlan: number;
    activatedPlan: number;
    createdMultiplePlans: number;
    swappedMeal: number;
    activeLast7Days: number;
  }>;
  retentionTriangle: Array<{
    weekStart: string;
    signups: number;
    activeByWeek: Array<number | null>;
  }>;
};

// ---------------------------------------------------------------------------
// Theme: a colorblind-validated palette (adjacent-pair CVD dE >= 8 in both
// modes), with the sequential ramp reversed in dark mode so low values recede
// into the dark surface the way pale cells recede into the light one.
// ---------------------------------------------------------------------------

type ChartTheme = {
  series1: string;
  series2: string;
  deemph: string;
  grid: string;
  axis: string;
  muted: string;
  ink: string;
  // sequential ramp low -> high, plus a readable ink color per step
  ramp: string[];
  rampInk: string[];
};

const LIGHT_THEME: ChartTheme = {
  series1: '#2a78d6',
  series2: '#eb6834',
  deemph: '#b9b7af',
  grid: '#e1e0d9',
  axis: '#c3c2b7',
  muted: '#898781',
  ink: 'var(--mantine-color-text)',
  ramp: ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#104281'],
  rampInk: ['#0b0b0b', '#0b0b0b', '#0b0b0b', '#ffffff', '#ffffff', '#ffffff'],
};

const DARK_THEME: ChartTheme = {
  series1: '#3987e5',
  series2: '#d95926',
  deemph: '#52514e',
  grid: '#2c2c2a',
  axis: '#383835',
  muted: '#898781',
  ink: 'var(--mantine-color-text)',
  ramp: ['#0d366b', '#184f95', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4'],
  rampInk: ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#0b0b0b', '#0b0b0b'],
};

const useChartTheme = (): ChartTheme => {
  const scheme = useComputedColorScheme('light');
  return scheme === 'dark' ? DARK_THEME : LIGHT_THEME;
};

const pct = (count: number, total: number) =>
  total > 0 ? `${Math.round((count / total) * 100)}%` : '–';

// Short "Jun 23" style label from a "YYYY-MM-DD" week start.
const weekLabel = (weekStart: string) => {
  const d = new Date(weekStart + 'T00:00:00Z');
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

// Wide charts scroll inside their card instead of squishing on phones.
const ChartScroll = ({ minWidth, children }: { minWidth: number; children: ReactNode }) => (
  <Box style={{ overflowX: 'auto' }}>
    <Box style={{ minWidth }}>{children}</Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Activation funnel — horizontal bars, one hue, share of all signups
// ---------------------------------------------------------------------------

export const ActivationFunnel = ({ stats }: { stats: AdminUserStats }) => {
  const theme = useChartTheme();
  const stages = [
    { label: 'Signed up', value: stats.totals.users },
    { label: 'Completed onboarding', value: stats.totals.completedOnboarding },
    { label: 'Created a plan', value: stats.totals.createdPlan },
    { label: 'Activated a plan', value: stats.totals.activatedPlan },
    { label: 'Created 2+ plans', value: stats.totals.createdMultiplePlans },
    { label: 'Swapped a meal', value: stats.totals.swappedMeal },
  ];
  const max = Math.max(1, stages[0].value);
  const W = 920;
  const rowH = 42;
  const labelW = 185;
  const valueW = 120;
  const barMax = W - labelW - valueW;
  const H = stages.length * rowH + 6;

  return (
    <ChartScroll minWidth={620}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Activation funnel">
        {stages.map((stage, i) => {
          const y = i * rowH + 6;
          const barW = Math.max(3, (stage.value / max) * barMax);
          const prev = i === 0 ? null : stages[i - 1];
          const step =
            prev && prev.value > 0
              ? `${Math.round((stage.value / prev.value) * 100)}% of "${prev.label}"`
              : null;
          return (
            <g key={stage.label}>
              <text
                x={labelW - 12}
                y={y + 16}
                textAnchor="end"
                fontSize={12.5}
                fill="var(--mantine-color-dimmed)"
              >
                {stage.label}
              </text>
              <Tooltip
                label={`${stage.value} users · ${pct(stage.value, max)} of signups${step ? ` · ${step}` : ''}`}
              >
                <g tabIndex={0}>
                  <rect x={labelW} y={y} width={barW} height={22} rx={4} fill={theme.series1} />
                  <rect
                    x={labelW}
                    y={y}
                    width={Math.min(5, barW)}
                    height={22}
                    fill={theme.series1}
                  />
                  <text
                    x={labelW + barW + 10}
                    y={y + 16}
                    fontSize={12.5}
                    fontWeight={650}
                    fill={theme.ink}
                  >
                    {stage.value} · {pct(stage.value, max)}
                  </text>
                </g>
              </Tooltip>
            </g>
          );
        })}
      </svg>
    </ChartScroll>
  );
};

// ---------------------------------------------------------------------------
// Cohort milestone heatmap — rows are signup weeks, columns are milestones
// ---------------------------------------------------------------------------

const MILESTONE_COLS: Array<{
  label: string;
  get: (c: AdminUserStats['weeklyCohorts'][number]) => number;
}> = [
  { label: 'Onboarded', get: (c) => c.completedOnboarding },
  { label: 'Created plan', get: (c) => c.createdPlan },
  { label: 'Activated', get: (c) => c.activatedPlan },
  { label: '2+ plans', get: (c) => c.createdMultiplePlans },
  { label: 'Swapped', get: (c) => c.swappedMeal },
  { label: 'Active 7d', get: (c) => c.activeLast7Days },
];

// Fixed bins so a cell's color means the same percentage everywhere.
const heatBin = (v: number) =>
  v >= 65 ? 5 : v >= 50 ? 4 : v >= 35 ? 3 : v >= 20 ? 2 : v >= 10 ? 1 : 0;

export const CohortHeatmap = ({ cohorts }: { cohorts: AdminUserStats['weeklyCohorts'] }) => {
  const theme = useChartTheme();
  // API is most-recent-first; render oldest at the top.
  const rows = [...cohorts].reverse();
  const cellW = 120;
  const cellH = 32;
  const labelW = 68;
  const headH = 24;
  const W = labelW + MILESTONE_COLS.length * cellW;
  const H = headH + rows.length * cellH;

  return (
    <ChartScroll minWidth={700}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Cohort milestone heatmap">
        {MILESTONE_COLS.map((col, c) => (
          <text
            key={col.label}
            x={labelW + c * cellW + cellW / 2}
            y={15}
            textAnchor="middle"
            fontSize={11.5}
            fontWeight={600}
            fill="var(--mantine-color-dimmed)"
          >
            {col.label}
          </text>
        ))}
        {rows.map((cohort, r) => {
          const y = headH + r * cellH;
          return (
            <g key={cohort.weekStart}>
              <text
                x={labelW - 10}
                y={y + cellH / 2 + 4}
                textAnchor="end"
                fontSize={11.5}
                fill={theme.muted}
              >
                {weekLabel(cohort.weekStart)}
              </text>
              {MILESTONE_COLS.map((col, c) => {
                const share =
                  cohort.signups > 0
                    ? Math.round((col.get(cohort) / cohort.signups) * 100)
                    : 0;
                const bin = heatBin(share);
                return (
                  <Tooltip
                    key={col.label}
                    label={`Week of ${weekLabel(cohort.weekStart)}: ${col.get(cohort)} of ${cohort.signups} (${share}%) ${col.label.toLowerCase()}`}
                  >
                    <g tabIndex={0}>
                      <rect
                        x={labelW + c * cellW + 1}
                        y={y + 1}
                        width={cellW - 2}
                        height={cellH - 2}
                        rx={3}
                        fill={cohort.signups > 0 ? theme.ramp[bin] : 'transparent'}
                        stroke={cohort.signups > 0 ? 'none' : theme.grid}
                      />
                      <text
                        x={labelW + c * cellW + cellW / 2}
                        y={y + cellH / 2 + 4}
                        textAnchor="middle"
                        fontSize={11.5}
                        fontWeight={600}
                        fill={cohort.signups > 0 ? theme.rampInk[bin] : theme.muted}
                      >
                        {cohort.signups > 0 ? `${share}%` : '–'}
                      </text>
                    </g>
                  </Tooltip>
                );
              })}
            </g>
          );
        })}
      </svg>
    </ChartScroll>
  );
};

// ---------------------------------------------------------------------------
// Column chart — signups per week, plans-created distribution
// ---------------------------------------------------------------------------

export const ColumnChart = ({
  labels,
  values,
  unit,
  ariaLabel,
}: {
  labels: string[];
  values: number[];
  unit: string;
  ariaLabel: string;
}) => {
  const theme = useChartTheme();
  const W = 460;
  const H = 200;
  const padL = 38;
  const padB = 28;
  const padT = 12;
  const plotW = W - padL - 8;
  const plotH = H - padT - padB;
  const rawMax = Math.max(1, ...values);
  const step = Math.max(1, Math.ceil(rawMax / 4));
  const max = step * 4;
  const band = plotW / values.length;
  // Dense windows (52 weeks) make the band narrower than the 8-unit gap;
  // clamp so the bar width never goes negative (SVG drops such rects).
  const barW = Math.max(2, Math.min(24, band - 8));
  // Label every column when they fit, else thin to every other / fewer.
  const labelEvery = Math.ceil(labels.length / 8);

  return (
    <ChartScroll minWidth={340}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel}>
        {[0, 1, 2, 3, 4].map((i) => {
          const gv = i * step;
          const y = padT + plotH - (gv / max) * plotH;
          return (
            <g key={gv}>
              <line x1={padL} x2={padL + plotW} y1={y} y2={y} stroke={theme.grid} strokeWidth={1} />
              <text
                x={padL - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10.5}
                fill={theme.muted}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {gv}
              </text>
            </g>
          );
        })}
        <line
          x1={padL}
          x2={padL + plotW}
          y1={padT + plotH}
          y2={padT + plotH}
          stroke={theme.axis}
          strokeWidth={1}
        />
        {values.map((v, i) => {
          const x = padL + i * band + (band - barW) / 2;
          const h = (v / max) * plotH;
          const y = padT + plotH - h;
          return (
            <g key={i}>
              <Tooltip label={`${labels[i]}: ${v} ${unit}`}>
                <g tabIndex={0}>
                  <rect x={x} y={y} width={barW} height={h} rx={4} fill={theme.series1} />
                  <rect
                    x={x}
                    y={padT + plotH - Math.min(5, h)}
                    width={barW}
                    height={Math.min(5, h)}
                    fill={theme.series1}
                  />
                </g>
              </Tooltip>
              {i % labelEvery === 0 && (
                <text
                  x={x + barW / 2}
                  y={H - 8}
                  textAnchor="middle"
                  fontSize={10.5}
                  fill={theme.muted}
                >
                  {labels[i]}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </ChartScroll>
  );
};

// ---------------------------------------------------------------------------
// Activation-rate lines — created% and activated% by signup cohort
// ---------------------------------------------------------------------------

export const ActivationRateLines = ({ cohorts }: { cohorts: AdminUserStats['weeklyCohorts'] }) => {
  const theme = useChartTheme();
  const rows = [...cohorts].reverse();
  const rateOf = (c: AdminUserStats['weeklyCohorts'][number], count: number) =>
    c.signups > 0 ? Math.round((count / c.signups) * 100) : 0;
  const series = [
    {
      label: 'Created a plan',
      color: theme.series1,
      data: rows.map((c) => rateOf(c, c.createdPlan)),
    },
    {
      label: 'Activated a plan',
      color: theme.series2,
      data: rows.map((c) => rateOf(c, c.activatedPlan)),
    },
  ];

  const W = 460;
  const H = 210;
  const padL = 38;
  const padR = 46;
  const padB = 28;
  const padT = 12;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(40, Math.ceil(Math.max(...series.flatMap((s) => s.data)) / 20) * 20);
  const xFor = (i: number) => padL + (rows.length > 1 ? (i / (rows.length - 1)) * plotW : 0);
  const yFor = (v: number) => padT + plotH - (v / max) * plotH;
  const xLabelIdx = rows.length > 2 ? [0, Math.floor((rows.length - 1) / 2), rows.length - 1] : [0];

  return (
    <div>
      <ChartScroll minWidth={340}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label="Created and activated plan rates by signup cohort"
        >
          {Array.from({ length: max / 20 + 1 }, (_, i) => i * 20).map((gv) => (
            <g key={gv}>
              <line
                x1={padL}
                x2={padL + plotW}
                y1={yFor(gv)}
                y2={yFor(gv)}
                stroke={theme.grid}
                strokeWidth={1}
              />
              <text
                x={padL - 6}
                y={yFor(gv) + 4}
                textAnchor="end"
                fontSize={10.5}
                fill={theme.muted}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {gv}%
              </text>
            </g>
          ))}
          {series.map((s) => {
            const d = s.data
              .map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`)
              .join(' ');
            const last = s.data[s.data.length - 1];
            return (
              <g key={s.label}>
                <path
                  d={d}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
                <circle
                  cx={xFor(s.data.length - 1)}
                  cy={yFor(last)}
                  r={4}
                  fill={s.color}
                  stroke="var(--mantine-color-body)"
                  strokeWidth={2}
                />
                <text
                  x={xFor(s.data.length - 1) + 9}
                  y={yFor(last) + 4}
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--mantine-color-dimmed)"
                >
                  {last}%
                </text>
              </g>
            );
          })}
          {xLabelIdx.map((i) => (
            <text
              key={i}
              x={xFor(i)}
              y={H - 8}
              textAnchor="middle"
              fontSize={10.5}
              fill={theme.muted}
            >
              {weekLabel(rows[i].weekStart)}
            </text>
          ))}
          {/* invisible per-week hit areas so hover reads out both series */}
          {rows.map((c, i) => (
            <Tooltip
              key={c.weekStart}
              label={`Week of ${weekLabel(c.weekStart)}: created ${series[0].data[i]}%, activated ${series[1].data[i]}% (${c.signups} signups)`}
            >
              <rect
                tabIndex={0}
                x={xFor(i) - (plotW / Math.max(1, rows.length - 1)) / 2}
                y={padT}
                width={plotW / Math.max(1, rows.length - 1)}
                height={plotH}
                fill="transparent"
              />
            </Tooltip>
          ))}
        </svg>
      </ChartScroll>
      <ChartLegend
        items={series.map((s) => ({ label: s.label, color: s.color, kind: 'line' }))}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Swappers vs everyone else — emphasis grouped bars
// ---------------------------------------------------------------------------

export const SwapComparisonChart = ({
  comparison,
}: {
  comparison: AdminUserStats['swapComparison'];
}) => {
  const theme = useChartTheme();
  const measures: Array<{ label: string; get: (s: SwapCohortStats) => number }> = [
    { label: 'Active last 30d', get: (s) => s.activeLast30Days },
    { label: 'Created 2+ plans', get: (s) => s.createdMultiplePlans },
    { label: 'Still subscribed', get: (s) => s.stillSubscribed },
  ];
  const seriesDef = [
    { label: `Swapped a meal (${comparison.swapped.users})`, color: theme.series1, stats: comparison.swapped },
    { label: `Never swapped (${comparison.notSwapped.users})`, color: theme.deemph, stats: comparison.notSwapped },
  ];

  const W = 460;
  const H = 216;
  const padL = 40;
  const padB = 42;
  const padT = 14;
  const plotW = W - padL - 8;
  const plotH = H - padT - padB;
  const band = plotW / measures.length;
  const barW = 22;
  const gap = 6;

  return (
    <div>
      <ChartScroll minWidth={340}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label="Retention measures for swappers versus non-swappers"
        >
          {[0, 25, 50, 75, 100].map((gv) => {
            const y = padT + plotH - (gv / 100) * plotH;
            return (
              <g key={gv}>
                <line x1={padL} x2={padL + plotW} y1={y} y2={y} stroke={theme.grid} strokeWidth={1} />
                <text
                  x={padL - 6}
                  y={y + 4}
                  textAnchor="end"
                  fontSize={10.5}
                  fill={theme.muted}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {gv}%
                </text>
              </g>
            );
          })}
          {measures.map((m, i) => {
            const groupX = padL + i * band + band / 2 - barW - gap / 2;
            return (
              <g key={m.label}>
                {seriesDef.map((s, si) => {
                  const count = m.get(s.stats);
                  const share =
                    s.stats.users > 0 ? Math.round((count / s.stats.users) * 100) : 0;
                  const h = (share / 100) * plotH;
                  const x = groupX + si * (barW + gap);
                  const y = padT + plotH - h;
                  return (
                    <Tooltip
                      key={s.label}
                      label={`${m.label}: ${count} of ${s.stats.users} (${share}%) — ${si === 0 ? 'swapped a meal' : 'never swapped'}`}
                    >
                      <g tabIndex={0}>
                        <rect x={x} y={y} width={barW} height={h} rx={4} fill={s.color} />
                        <rect
                          x={x}
                          y={padT + plotH - Math.min(5, h)}
                          width={barW}
                          height={Math.min(5, h)}
                          fill={s.color}
                        />
                        <text
                          x={x + barW / 2}
                          y={y - 5}
                          textAnchor="middle"
                          fontSize={10.5}
                          fontWeight={650}
                          fill="var(--mantine-color-dimmed)"
                        >
                          {share}%
                        </text>
                      </g>
                    </Tooltip>
                  );
                })}
                <text
                  x={padL + i * band + band / 2}
                  y={H - 22}
                  textAnchor="middle"
                  fontSize={10.5}
                  fill={theme.muted}
                >
                  {m.label.split(' ').slice(0, 2).join(' ')}
                </text>
                <text
                  x={padL + i * band + band / 2}
                  y={H - 9}
                  textAnchor="middle"
                  fontSize={10.5}
                  fill={theme.muted}
                >
                  {m.label.split(' ').slice(2).join(' ')}
                </text>
              </g>
            );
          })}
        </svg>
      </ChartScroll>
      <ChartLegend
        items={seriesDef.map((s) => ({ label: s.label, color: s.color, kind: 'rect' }))}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Retention triangle — cohorts x weeks-since-signup, from activeWeeks stamps
// ---------------------------------------------------------------------------

export const RetentionTriangle = ({
  triangle,
}: {
  triangle: AdminUserStats['retentionTriangle'];
}) => {
  const theme = useChartTheme();
  const cols = triangle.length > 0 ? triangle[0].activeByWeek.length : 0;
  const cellW = 110;
  const cellH = 32;
  const labelW = 68;
  const headH = 24;
  const W = labelW + cols * cellW;
  const H = headH + triangle.length * cellH;
  const bin = (v: number) =>
    v >= 90 ? 5 : v >= 55 ? 4 : v >= 40 ? 3 : v >= 25 ? 2 : v >= 10 ? 1 : 0;

  return (
    <ChartScroll minWidth={660}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Weekly retention triangle">
        {Array.from({ length: cols }, (_, c) => (
          <text
            key={c}
            x={labelW + c * cellW + cellW / 2}
            y={15}
            textAnchor="middle"
            fontSize={11.5}
            fontWeight={600}
            fill="var(--mantine-color-dimmed)"
          >
            {c === 0 ? 'Signup week' : `+${c}w`}
          </text>
        ))}
        {triangle.map((row, r) => {
          const y = headH + r * cellH;
          return (
            <g key={row.weekStart}>
              <text
                x={labelW - 10}
                y={y + cellH / 2 + 4}
                textAnchor="end"
                fontSize={11.5}
                fill={theme.muted}
              >
                {weekLabel(row.weekStart)}
              </text>
              {row.activeByWeek.map((count, c) => {
                if (count === null) return null;
                const share = row.signups > 0 ? Math.round((count / row.signups) * 100) : 0;
                const b = bin(share);
                return (
                  <Tooltip
                    key={c}
                    label={`Cohort of ${weekLabel(row.weekStart)}: ${count} of ${row.signups} active ${c === 0 ? 'in their signup week' : `${c} week${c > 1 ? 's' : ''} later`} (${share}%)`}
                  >
                    <g tabIndex={0}>
                      <rect
                        x={labelW + c * cellW + 1}
                        y={y + 1}
                        width={cellW - 2}
                        height={cellH - 2}
                        rx={3}
                        fill={row.signups > 0 ? theme.ramp[b] : 'transparent'}
                        stroke={row.signups > 0 ? 'none' : theme.grid}
                      />
                      <text
                        x={labelW + c * cellW + cellW / 2}
                        y={y + cellH / 2 + 4}
                        textAnchor="middle"
                        fontSize={11.5}
                        fontWeight={600}
                        fill={row.signups > 0 ? theme.rampInk[b] : theme.muted}
                      >
                        {row.signups > 0 ? `${share}%` : '–'}
                      </text>
                    </g>
                  </Tooltip>
                );
              })}
            </g>
          );
        })}
      </svg>
    </ChartScroll>
  );
};

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

export const ChartLegend = ({
  items,
}: {
  items: Array<{ label: string; color: string; kind: 'line' | 'rect' }>;
}) => (
  <Box style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 6 }}>
    {items.map((item) => (
      <Box key={item.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            background: item.color,
            width: item.kind === 'line' ? 16 : 12,
            height: item.kind === 'line' ? 2 : 12,
            borderRadius: item.kind === 'line' ? 1 : 3,
          }}
        />
        <Text size="xs" c="dimmed">
          {item.label}
        </Text>
      </Box>
    ))}
  </Box>
);
